'use client';

import type { IntentHandlers } from '@morfi/backend-sdk-v2/client';
import { useLayoutEffect, useRef } from 'react';

import { type AppRouter, useAppRouter } from '@/lib/network/use-app-router';
import { useChartIntentStore } from '@/stores/chart-intent';

// Next's `PrefetchKind` enum ('auto' | 'full') is not exported from a public
// path, so we pin the literal value and assert against the router's own
// options type. `kind: 'full'` makes `router.prefetch` fetch the FULL route
// RSC — including the SSR-dehydrated SDK cache — which is the real route
// prefetch. The DEFAULT (`auto`) kind only prefetches to the nearest
// `loading.tsx` (the shell), which is why pure `router.prefetch(href)` /
// SDK data-cache warming still flashed a skeleton on click. See
// `.claude/rules/prefetching.md`.
type PrefetchOpts = NonNullable<Parameters<AppRouter['prefetch']>[1]>;
const FULL_KIND: PrefetchOpts['kind'] = 'full' as PrefetchOpts['kind'];

/**
 * Fire a FULL Next route prefetch for a network-aware href. The destination
 * then renders data-complete with zero loading state on click — a true route
 * prefetch, not React-Query cache warming. This is the ONE place that calls
 * `router.prefetch` with the full kind (FQ-147 allowlists this file).
 *
 * On Next 16 this ALSO preloads the destination route's JS chunks, so the
 * very first detail visit is fast too — no separate `modulepreload` needed.
 * Why: in 16 `router.prefetch` routes through the Segment Cache, which decodes
 * the Full prefetch RSC with React's Flight client as the bytes stream in
 * (`createFromNextReadableStream` → `react-server-dom-webpack/client`). Every
 * client-reference module row ("I" row) in that payload eagerly hits
 * `preloadModule` → `__webpack_chunk_load__`, fetching the route template's
 * page chunks during the prefetch (deduped by chunk id in React's own
 * `chunkCache`, so dynamic segments like `/markets/[pubkey]` pay it once per
 * session). `<Link prefetch={true}>` uses the identical Segment Cache path —
 * there is no separate chunk-preload it does that this call misses. The old
 * "full prefetch caches RSC+data but not JS chunks" limit was a Next 15.5
 * router-reducer behaviour and no longer applies. See
 * `.claude/rules/prefetching.md`.
 */
export function prefetchRouteFull(router: AppRouter, href: string): void {
  if (href.length === 0) return;
  router.prefetch(href, { kind: FULL_KIND });
}

/**
 * Hover/focus/touch intent that fires a FULL route prefetch (plus an optional
 * additive DATA-warm for any client-only queries not covered by the route's
 * SSR set). Drop-in for the SDK's `useIntentPrefetch`, which only warmed the
 * React-Query data cache and so left the route's `loading.tsx` skeleton
 * flashing on click.
 *
 * Pair with `<Link prefetch={false}>`: nothing prefetches on viewport (no
 * mount storm), and the full prefetch happens ONLY on real intent. Pass
 * `href === undefined` to skip the route prefetch (e.g. external / new-tab
 * targets) while still running the data-warm.
 */
export function useIntentRoutePrefetch(
  href: string | undefined,
  dataWarm?: () => void,
): IntentHandlers {
  const router = useAppRouter();
  const chartIntent = useChartIntentStore();
  // Latest-closure ref so the returned handler identity stays stable across
  // renders (the `dataWarm` closure changes every render).
  const warmRef = useRef(dataWarm);
  useLayoutEffect(() => {
    warmRef.current = dataWarm;
  }, [dataWarm]);

  function fire(): void {
    if (href !== undefined && href.length > 0) prefetchRouteFull(router, href);
    if (href !== undefined) chartIntent.getState().warm(href);
    warmRef.current?.();
  }

  return { onMouseEnter: fire, onFocus: fire, onPointerDown: fire };
}
